import { Ionicons } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import GalaxyHeaderBackground from '../components/GalaxyHeaderBackground';
import {
  ActivityIndicator,
  Alert,
  FlatList,
  Image,
  Modal,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { kioskApi, type Kiosk, type KioskScreenshot } from '../services/api';
import logger from '../utils/logger';
import { canManageSitesAndZones, canManageUsers } from '../utils/rbac';

const ONLINE_WINDOW_MS = 2 * 60 * 1000;
type KioskStatusFilter = 'all' | 'online' | 'offline';
type KioskModeFilter = 'all' | 'display' | 'meeting_room' | 'room_display' | 'gateway';

function modeLabel(mode: string | undefined, isArabic: boolean) {
  const labels: Record<string, [string, string]> = {
    display: ['عرض الحساسات', 'Sensor display'],
    gateway: ['بوابة LoRa', 'LoRa gateway'],
    hybrid: ['هجين', 'Hybrid'],
    meeting_room: ['غرفة اجتماع', 'Meeting room'],
    room_display: ['عرض غرفة/طابور', 'Room display'],
    patient_experience: ['تجربة المريض', 'Patient experience'],
  };
  const entry = labels[mode || 'display'] ?? labels.display;
  return isArabic ? entry[0] : entry[1];
}

function isOnline(kiosk: Kiosk) {
  if (!kiosk.lastSeenAt) return false;
  return Date.now() - new Date(kiosk.lastSeenAt).getTime() < ONLINE_WINDOW_MS;
}

export default function KiosksManagementScreen({ navigation }: any) {
  const { i18n } = useTranslation();
  const { theme } = useTheme();
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  const isArabic = i18n.language === 'ar';
  const canManage = canManageSitesAndZones(user) || canManageUsers(user);

  const [kiosks, setKiosks] = useState<Kiosk[]>([]);
  const [selected, setSelected] = useState<Kiosk | null>(null);
  const [screenshot, setScreenshot] = useState<KioskScreenshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [hardwareModel, setHardwareModel] = useState('');
  const [otaApkUrl, setOtaApkUrl] = useState('');
  const [otaVersion, setOtaVersion] = useState('');
  const [otaSha256, setOtaSha256] = useState('');
  const [otaNotes, setOtaNotes] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<KioskStatusFilter>('all');
  const [modeFilter, setModeFilter] = useState<KioskModeFilter>('all');

  const stats = useMemo(() => {
    const online = kiosks.filter(isOnline).length;
    return { total: kiosks.length, online, offline: kiosks.length - online };
  }, [kiosks]);

  const filteredKiosks = useMemo(() => {
    const term = query.trim().toLowerCase();
    return kiosks.filter((kiosk) => {
      const online = isOnline(kiosk);
      const mode = String(kiosk.displayConfig?.mode || 'display');
      if (statusFilter === 'online' && !online) return false;
      if (statusFilter === 'offline' && online) return false;
      if (modeFilter !== 'all' && mode !== modeFilter) return false;
      if (!term) return true;
      return [
        kiosk.name,
        kiosk.displayCode,
        kiosk.serialNumber,
        kiosk.hardwareModel,
        kiosk.zone?.name,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [kiosks, modeFilter, query, statusFilter]);

  const loadKiosks = useCallback(async () => {
    try {
      setLoading(true);
      const data = await kioskApi.list();
      setKiosks(Array.isArray(data) ? data : []);
    } catch (error: any) {
      logger.error('Failed to load kiosks:', error);
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        error?.response?.data?.message ||
          (isArabic ? 'تعذر تحميل الأكشاك' : 'Unable to load kiosks'),
      );
    } finally {
      setLoading(false);
    }
  }, [isArabic]);

  useEffect(() => {
    navigation.setOptions({
      title: isArabic ? 'إدارة الأكشاك' : 'Kiosk Management',
      headerTintColor: '#ffffff',
      headerTitleStyle: { color: '#ffffff', fontWeight: '700' },
      headerRight: () =>
        canManage ? (
          <TouchableOpacity style={{ marginRight: 16 }} onPress={() => setShowCreate(true)}>
            <Ionicons name="add-circle" size={28} color="#ffffff" />
          </TouchableOpacity>
        ) : null,
    });
  }, [navigation, isArabic, canManage]);

  useEffect(() => {
    if (!canManage) {
      Alert.alert(
        isArabic ? 'صلاحية مطلوبة' : 'Permission Required',
        isArabic
          ? 'لا تملك صلاحية لإدارة الأكشاك من التطبيق.'
          : 'You do not have permission to manage kiosks from the app.',
        [{ text: isArabic ? 'حسناً' : 'OK', onPress: () => navigation.goBack() }],
      );
      return;
    }
    loadKiosks();
  }, [canManage, isArabic, navigation, loadKiosks]);

  const refresh = async () => {
    setRefreshing(true);
    await loadKiosks();
    setRefreshing(false);
  };

  const runAction = async (key: string, action: () => Promise<void>, done?: string) => {
    if (!selected) return;
    try {
      setBusyAction(key);
      await action();
      const fresh = await kioskApi.getById(selected.id);
      setSelected(fresh);
      setKiosks((items) => items.map((item) => (item.id === fresh.id ? fresh : item)));
      if (done) Alert.alert(isArabic ? 'تم' : 'Done', done);
    } catch (error: any) {
      logger.error(`Kiosk action failed: ${key}`, error);
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        error?.response?.data?.message || (isArabic ? 'فشل تنفيذ الأمر' : 'Command failed'),
      );
    } finally {
      setBusyAction(null);
    }
  };

  const createKiosk = async () => {
    if (!name.trim()) {
      Alert.alert(isArabic ? 'خطأ' : 'Error', isArabic ? 'أدخل اسم الكشك' : 'Enter kiosk name');
      return;
    }

    try {
      setBusyAction('create');
      const created = await kioskApi.create({
        name: name.trim(),
        orgId: user?.orgId,
        serialNumber: serialNumber.trim() || undefined,
        hardwareModel: hardwareModel.trim() || undefined,
      });
      setKiosks((items) => [created, ...items]);
      setShowCreate(false);
      setName('');
      setSerialNumber('');
      setHardwareModel('');
      Alert.alert(
        isArabic ? 'تم إنشاء الكشك' : 'Kiosk created',
        created.displayCode
          ? `${isArabic ? 'كود التفعيل' : 'Activation code'}: ${created.displayCode}`
          : isArabic
          ? 'تم إنشاء الكشك بنجاح'
          : 'Kiosk created successfully',
      );
    } catch (error: any) {
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        error?.response?.data?.message ||
          (isArabic ? 'تعذر إنشاء الكشك' : 'Unable to create kiosk'),
      );
    } finally {
      setBusyAction(null);
    }
  };

  const copyCode = async (code?: string | null) => {
    if (!code) return;
    await Clipboard.setStringAsync(code);
    Alert.alert(isArabic ? 'تم النسخ' : 'Copied', code);
  };

  const requestAndLoadScreenshot = async () => {
    if (!selected) return;
    await runAction(
      'screenshot',
      async () => {
        await kioskApi.requestScreenshot(selected.id);
        await new Promise((resolve) => setTimeout(resolve, 2200));
        const snap = await kioskApi.getScreenshot(selected.id);
        setScreenshot(snap);
      },
      isArabic ? 'تم طلب لقطة الشاشة' : 'Screenshot requested',
    );
  };

  const sendOtaUpdate = async () => {
    if (!selected) return;
    if (!otaApkUrl.trim() || !otaVersion.trim()) {
      Alert.alert(
        isArabic ? 'بيانات ناقصة' : 'Missing data',
        isArabic ? 'أدخل رابط APK ورقم الإصدار' : 'Enter the APK URL and version',
      );
      return;
    }

    Alert.alert(
      isArabic ? 'إرسال تحديث هوائي' : 'Send OTA update',
      isArabic
        ? `سيتم إرسال تحديث ${otaVersion.trim()} إلى ${selected.name}`
        : `Send version ${otaVersion.trim()} to ${selected.name}?`,
      [
        { text: isArabic ? 'إلغاء' : 'Cancel', style: 'cancel' },
        {
          text: isArabic ? 'إرسال' : 'Send',
          onPress: () =>
            runAction(
              'ota',
              () =>
                kioskApi
                  .otaUpdate(selected.id, {
                    apkUrl: otaApkUrl.trim(),
                    version: otaVersion.trim(),
                    notes: otaNotes.trim() || undefined,
                    expectedSha256: otaSha256.trim() || undefined,
                  })
                  .then(() => undefined),
              isArabic ? 'تم إرسال التحديث' : 'OTA update queued',
            ),
        },
      ],
    );
  };

  const cancelOtaUpdate = async () => {
    if (!selected) return;
    Alert.alert(
      isArabic ? 'إلغاء التحديث' : 'Cancel update',
      isArabic ? 'هل تريد إلغاء التحديث المعلق؟' : 'Cancel the pending update?',
      [
        { text: isArabic ? 'لا' : 'No', style: 'cancel' },
        {
          text: isArabic ? 'إلغاء التحديث' : 'Cancel update',
          style: 'destructive',
          onPress: () =>
            runAction(
              'cancel-ota',
              () => kioskApi.cancelOtaUpdate(selected.id).then(() => undefined),
              isArabic ? 'تم إلغاء التحديث' : 'OTA update cancelled',
            ),
        },
      ],
    );
  };

  const loadLatestScreenshot = async () => {
    if (!selected) return;
    await runAction('latest-screenshot', async () => {
      const snap = await kioskApi.getScreenshot(selected.id);
      setScreenshot(snap);
    });
  };

  const commandButton = (
    label: string,
    icon: keyof typeof Ionicons.glyphMap,
    type: string,
    payload?: Record<string, any>,
  ) => (
    <TouchableOpacity
      style={[styles.commandButton, { borderColor: theme.colors.border }]}
      onPress={() =>
        runAction(
          `${type}:${label}`,
          () => kioskApi.sendCommand(selected!.id, { type, payload }).then(() => undefined),
          isArabic ? 'تم إرسال الأمر' : 'Command sent',
        )
      }
      disabled={!!busyAction}
    >
      <Ionicons name={icon} size={18} color={theme.colors.primary} />
      <Text style={[styles.commandText, { color: theme.colors.text }]}>{label}</Text>
    </TouchableOpacity>
  );

  const renderKiosk = ({ item }: { item: Kiosk }) => {
    const online = isOnline(item);
    const mode = item.displayConfig?.mode || 'display';
    return (
      <TouchableOpacity
        style={[
          styles.card,
          { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
        ]}
        onPress={() => {
          setSelected(item);
          setScreenshot(null);
        }}
      >
        <View style={styles.cardTop}>
          <View style={styles.cardTitleRow}>
            <View
              style={[
                styles.statusDot,
                { backgroundColor: online ? theme.colors.success : theme.colors.error },
              ]}
            />
            <Text style={[styles.cardTitle, { color: theme.colors.text }]} numberOfLines={1}>
              {item.name}
            </Text>
          </View>
          <Text
            style={[styles.badge, { color: online ? theme.colors.success : theme.colors.error }]}
          >
            {online ? (isArabic ? 'متصل' : 'Online') : isArabic ? 'غير متصل' : 'Offline'}
          </Text>
        </View>
        <Text style={[styles.meta, { color: theme.colors.textSecondary }]}>
          {modeLabel(mode, isArabic)}
          {item.zone?.name ? ` • ${item.zone.name}` : ''}
        </Text>
        <TouchableOpacity style={styles.codeRow} onPress={() => copyCode(item.displayCode)}>
          <Ionicons name="key-outline" size={16} color={theme.colors.primary} />
          <Text style={[styles.codeText, { color: theme.colors.text }]}>
            {item.displayCode || (isArabic ? 'لا يوجد كود' : 'No code')}
          </Text>
          <Ionicons name="copy-outline" size={16} color={theme.colors.textSecondary} />
        </TouchableOpacity>
      </TouchableOpacity>
    );
  };

  if (loading) {
    return (
      <View style={[styles.center, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.statsRow}>
        <Stat
          label={isArabic ? 'الإجمالي' : 'Total'}
          value={stats.total}
          color={theme.colors.primary}
          theme={theme}
        />
        <Stat
          label={isArabic ? 'متصل' : 'Online'}
          value={stats.online}
          color={theme.colors.success}
          theme={theme}
        />
        <Stat
          label={isArabic ? 'غير متصل' : 'Offline'}
          value={stats.offline}
          color={theme.colors.error}
          theme={theme}
        />
      </View>

      <View style={styles.filterPanel}>
        <View
          style={[
            styles.searchBox,
            { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
          ]}
        >
          <Ionicons name="search" size={18} color={theme.colors.textSecondary} />
          <TextInput
            value={query}
            onChangeText={setQuery}
            style={[styles.searchInput, { color: theme.colors.text }]}
            placeholder={
              isArabic ? 'بحث بالاسم أو الكود أو السيريال' : 'Search name, code, or serial'
            }
            placeholderTextColor={theme.colors.textSecondary}
          />
          {query.length > 0 && (
            <TouchableOpacity onPress={() => setQuery('')}>
              <Ionicons name="close-circle" size={18} color={theme.colors.textSecondary} />
            </TouchableOpacity>
          )}
        </View>

        <View style={styles.chipRow}>
          {(
            [
              ['all', isArabic ? 'الكل' : 'All'],
              ['online', isArabic ? 'متصل' : 'Online'],
              ['offline', isArabic ? 'غير متصل' : 'Offline'],
            ] as [KioskStatusFilter, string][]
          ).map(([key, label]) => (
            <FilterChip
              key={key}
              label={label}
              active={statusFilter === key}
              onPress={() => setStatusFilter(key)}
              theme={theme}
            />
          ))}
        </View>

        <View style={styles.chipRow}>
          {(
            [
              ['all', isArabic ? 'كل الأنماط' : 'All modes'],
              ['display', isArabic ? 'حساسات' : 'Display'],
              ['meeting_room', isArabic ? 'اجتماعات' : 'Meeting'],
              ['room_display', isArabic ? 'غرفة/طابور' : 'Room display'],
              ['gateway', isArabic ? 'بوابة' : 'Gateway'],
            ] as [KioskModeFilter, string][]
          ).map(([key, label]) => (
            <FilterChip
              key={key}
              label={label}
              active={modeFilter === key}
              onPress={() => setModeFilter(key)}
              theme={theme}
            />
          ))}
        </View>
      </View>

      <FlatList
        data={filteredKiosks}
        keyExtractor={(item) => item.id}
        renderItem={renderKiosk}
        contentContainerStyle={styles.list}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={refresh} />}
        ListEmptyComponent={
          <View style={styles.empty}>
            <Ionicons
              name="tablet-landscape-outline"
              size={52}
              color={theme.colors.textSecondary}
            />
            <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>
              {isArabic ? 'لا توجد أكشاك مطابقة' : 'No matching kiosks'}
            </Text>
          </View>
        }
      />

      <Modal visible={!!selected} animationType="slide" onRequestClose={() => setSelected(null)}>
        <View style={[styles.modalRoot, { backgroundColor: theme.colors.background }]}>
          <View
            style={[
              styles.modalHeader,
              {
                backgroundColor: theme.colors.primary,
                paddingTop: Math.max(insets.top, 16),
                minHeight: Math.max(insets.top, 16) + 54,
              },
            ]}
          >
            <GalaxyHeaderBackground />
            <TouchableOpacity onPress={() => setSelected(null)}>
              <Ionicons name="close" size={26} color="#ffffff" />
            </TouchableOpacity>
            <Text style={styles.modalTitle}>{selected?.name}</Text>
            <TouchableOpacity onPress={refresh}>
              <Ionicons name="refresh" size={24} color="#ffffff" />
            </TouchableOpacity>
          </View>

          {selected && (
            <ScrollView
              style={styles.detailScroll}
              contentContainerStyle={[
                styles.detailContent,
                { paddingBottom: Math.max(insets.bottom + 24, 40) },
              ]}
            >
              <View
                style={[
                  styles.detailCard,
                  { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
                ]}
              >
                <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
                  {isArabic ? 'كود التفعيل' : 'Activation code'}
                </Text>
                <TouchableOpacity
                  style={[styles.bigCode, { backgroundColor: theme.colors.background }]}
                  onPress={() => copyCode(selected.displayCode)}
                >
                  <Text style={[styles.bigCodeText, { color: theme.colors.primary }]}>
                    {selected.displayCode || '------'}
                  </Text>
                  <Ionicons name="copy-outline" size={22} color={theme.colors.primary} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.primaryButton, { backgroundColor: theme.colors.primary }]}
                  disabled={!!busyAction}
                  onPress={() =>
                    runAction(
                      'regenerate',
                      () => kioskApi.regenerateCode(selected.id).then(() => undefined),
                      isArabic ? 'تم إصدار كود جديد' : 'New activation code issued',
                    )
                  }
                >
                  <Ionicons name="key" size={18} color="#ffffff" />
                  <Text style={styles.primaryButtonText}>
                    {isArabic ? 'إصدار كود جديد' : 'Issue new code'}
                  </Text>
                </TouchableOpacity>
              </View>

              <View
                style={[
                  styles.detailCard,
                  { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
                ]}
              >
                <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
                  {isArabic ? 'تحكم سريع' : 'Quick controls'}
                </Text>
                <View style={styles.commandGrid}>
                  {commandButton(
                    isArabic ? 'تحديث الإعدادات' : 'Reload config',
                    'sync-outline',
                    'reload_config',
                  )}
                  {commandButton(
                    isArabic ? 'إعادة تشغيل التطبيق' : 'Restart app',
                    'refresh-circle-outline',
                    'app_restart',
                  )}
                  {commandButton(
                    isArabic ? 'إطفاء الشاشة' : 'Screen off',
                    'moon-outline',
                    'screen',
                    { enable: false },
                  )}
                  {commandButton(
                    isArabic ? 'تشغيل الشاشة' : 'Screen on',
                    'sunny-outline',
                    'screen',
                    { enable: true },
                  )}
                  {commandButton(isArabic ? 'كتم الصوت' : 'Mute', 'volume-mute-outline', 'volume', {
                    value: 0,
                  })}
                  {commandButton(
                    isArabic ? 'صوت 80%' : 'Volume 80%',
                    'volume-high-outline',
                    'volume',
                    { value: 80 },
                  )}
                </View>
              </View>

              <View
                style={[
                  styles.detailCard,
                  { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
                ]}
              >
                <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
                  {isArabic ? 'لقطة الشاشة' : 'Screenshot'}
                </Text>
                <View style={styles.screenshotActions}>
                  <TouchableOpacity
                    style={[styles.secondaryButton, { borderColor: theme.colors.border }]}
                    onPress={requestAndLoadScreenshot}
                    disabled={!!busyAction}
                  >
                    <Ionicons name="camera-outline" size={18} color={theme.colors.primary} />
                    <Text style={[styles.secondaryButtonText, { color: theme.colors.text }]}>
                      {isArabic ? 'أخذ لقطة' : 'Capture'}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.secondaryButton, { borderColor: theme.colors.border }]}
                    onPress={loadLatestScreenshot}
                    disabled={!!busyAction}
                  >
                    <Ionicons name="image-outline" size={18} color={theme.colors.primary} />
                    <Text style={[styles.secondaryButtonText, { color: theme.colors.text }]}>
                      {isArabic ? 'آخر لقطة' : 'Latest'}
                    </Text>
                  </TouchableOpacity>
                </View>
                {screenshot?.data ? (
                  <>
                    <Image
                      style={styles.screenshot}
                      resizeMode="contain"
                      source={{
                        uri: `data:image/${screenshot.format || 'jpeg'};base64,${screenshot.data}`,
                      }}
                    />
                    <Text style={[styles.meta, { color: theme.colors.textSecondary }]}>
                      {screenshot.screenshotAt || ''}
                    </Text>
                  </>
                ) : (
                  <Text style={[styles.meta, { color: theme.colors.textSecondary }]}>
                    {isArabic ? 'لا توجد لقطة شاشة محفوظة' : 'No screenshot available'}
                  </Text>
                )}
              </View>

              <View
                style={[
                  styles.detailCard,
                  { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
                ]}
              >
                <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
                  {isArabic ? 'التحديث الهوائي APK' : 'APK OTA update'}
                </Text>
                <View style={[styles.otaStatus, { backgroundColor: theme.colors.background }]}>
                  <Text style={[styles.otaStatusText, { color: theme.colors.text }]}>
                    {isArabic ? 'الإصدار الحالي' : 'Current version'}:{' '}
                    {selected.currentVersion || '-'}
                  </Text>
                  <Text style={[styles.otaStatusText, { color: theme.colors.textSecondary }]}>
                    {isArabic ? 'حالة التحديث' : 'Update status'}:{' '}
                    {String(selected.updateStatus?.state || selected.updateStatus?.status || '-')}
                    {selected.updateStatus?.targetVersion
                      ? ` → ${selected.updateStatus.targetVersion}`
                      : ''}
                  </Text>
                </View>
                <Input
                  label={isArabic ? 'رابط ملف APK' : 'APK URL'}
                  value={otaApkUrl}
                  onChangeText={setOtaApkUrl}
                  theme={theme}
                />
                <Input
                  label={isArabic ? 'رقم الإصدار' : 'Version'}
                  value={otaVersion}
                  onChangeText={setOtaVersion}
                  theme={theme}
                />
                <Input
                  label={isArabic ? 'SHA-256 اختياري' : 'SHA-256 optional'}
                  value={otaSha256}
                  onChangeText={setOtaSha256}
                  theme={theme}
                />
                <Input
                  label={isArabic ? 'ملاحظات' : 'Notes'}
                  value={otaNotes}
                  onChangeText={setOtaNotes}
                  theme={theme}
                />
                <View style={styles.screenshotActions}>
                  <TouchableOpacity
                    style={[
                      styles.primaryButton,
                      { backgroundColor: theme.colors.primary, flex: 1 },
                    ]}
                    onPress={sendOtaUpdate}
                    disabled={!!busyAction}
                  >
                    <Ionicons name="cloud-upload-outline" size={18} color="#ffffff" />
                    <Text style={styles.primaryButtonText}>
                      {isArabic ? 'إرسال التحديث' : 'Send update'}
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.secondaryButton, { borderColor: theme.colors.border }]}
                    onPress={cancelOtaUpdate}
                    disabled={!!busyAction}
                  >
                    <Ionicons name="close-circle-outline" size={18} color={theme.colors.error} />
                    <Text style={[styles.secondaryButtonText, { color: theme.colors.error }]}>
                      {isArabic ? 'إلغاء' : 'Cancel'}
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>

              {busyAction && (
                <View style={styles.busyRow}>
                  <ActivityIndicator color={theme.colors.primary} />
                  <Text style={[styles.meta, { color: theme.colors.textSecondary }]}>
                    {isArabic ? 'جاري التنفيذ...' : 'Working...'}
                  </Text>
                </View>
              )}
            </ScrollView>
          )}
        </View>
      </Modal>

      <Modal
        visible={showCreate}
        transparent
        animationType="fade"
        onRequestClose={() => setShowCreate(false)}
      >
        <View style={styles.overlay}>
          <View style={[styles.createModal, { backgroundColor: theme.colors.card }]}>
            <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
              {isArabic ? 'إضافة كشك' : 'Add kiosk'}
            </Text>
            <Input
              label={isArabic ? 'اسم الكشك' : 'Kiosk name'}
              value={name}
              onChangeText={setName}
              theme={theme}
            />
            <Input
              label={isArabic ? 'الرقم التسلسلي' : 'Serial number'}
              value={serialNumber}
              onChangeText={setSerialNumber}
              theme={theme}
            />
            <Input
              label={isArabic ? 'موديل العتاد' : 'Hardware model'}
              value={hardwareModel}
              onChangeText={setHardwareModel}
              theme={theme}
            />
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.cancelButton} onPress={() => setShowCreate(false)}>
                <Text style={{ color: theme.colors.text }}>{isArabic ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.primaryButton, { backgroundColor: theme.colors.primary, flex: 1 }]}
                onPress={createKiosk}
                disabled={busyAction === 'create'}
              >
                <Text style={styles.primaryButtonText}>{isArabic ? 'إنشاء' : 'Create'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

function Stat({
  label,
  value,
  color,
  theme,
}: {
  label: string;
  value: number;
  color: string;
  theme: ReturnType<typeof useTheme>['theme'];
}) {
  return (
    <View
      style={[
        styles.stat,
        { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
      ]}
    >
      <Text style={[styles.statValue, { color }]}>{value}</Text>
      <Text style={[styles.statLabel, { color: theme.colors.textSecondary }]}>{label}</Text>
    </View>
  );
}

function FilterChip({
  label,
  active,
  onPress,
  theme,
}: {
  label: string;
  active: boolean;
  onPress: () => void;
  theme: { colors: { primary: string; card: string; border: string; text: string } };
}) {
  return (
    <TouchableOpacity
      style={[
        styles.filterChip,
        {
          backgroundColor: active ? theme.colors.primary : theme.colors.card,
          borderColor: active ? theme.colors.primary : theme.colors.border,
        },
      ]}
      onPress={onPress}
    >
      <Text style={[styles.filterText, { color: active ? '#f8fafc' : theme.colors.text }]}>
        {label}
      </Text>
    </TouchableOpacity>
  );
}

function Input(props: {
  label: string;
  value: string;
  onChangeText: (value: string) => void;
  theme: ReturnType<typeof useTheme>['theme'];
}) {
  return (
    <View style={{ marginTop: 12 }}>
      <Text style={[styles.inputLabel, { color: props.theme.colors.textSecondary }]}>
        {props.label}
      </Text>
      <TextInput
        value={props.value}
        onChangeText={props.onChangeText}
        style={[
          styles.input,
          {
            backgroundColor: props.theme.colors.background,
            borderColor: props.theme.colors.border,
            color: props.theme.colors.text,
          },
        ]}
        placeholder={props.label}
        placeholderTextColor={props.theme.colors.placeholder}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  statsRow: { flexDirection: 'row', gap: 10, padding: 16, paddingBottom: 8 },
  filterPanel: { paddingHorizontal: 16, paddingBottom: 8, gap: 8 },
  searchBox: {
    minHeight: 46,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  searchInput: { flex: 1, fontSize: 14, fontWeight: '700' },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  filterChip: { borderWidth: 1, borderRadius: 999, paddingHorizontal: 13, paddingVertical: 8 },
  filterText: { fontSize: 12, fontWeight: '800' },
  stat: {
    flex: 1,
    borderRadius: 12,
    borderWidth: 1,
    padding: 14,
    alignItems: 'center',
    elevation: 1,
  },
  statValue: { fontSize: 22, fontWeight: '800' },
  statLabel: { marginTop: 3, fontSize: 12, fontWeight: '600' },
  list: { padding: 16, paddingTop: 8, gap: 12 },
  card: { borderWidth: 1, borderRadius: 12, padding: 14 },
  cardTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  cardTitleRow: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8 },
  statusDot: { width: 10, height: 10, borderRadius: 5 },
  cardTitle: { flex: 1, fontSize: 16, fontWeight: '800' },
  badge: { fontSize: 12, fontWeight: '800' },
  meta: { fontSize: 12, marginTop: 8 },
  codeRow: { marginTop: 12, flexDirection: 'row', alignItems: 'center', gap: 8 },
  codeText: { fontSize: 15, fontWeight: '800', letterSpacing: 1 },
  empty: { alignItems: 'center', paddingTop: 80 },
  emptyText: { marginTop: 12, fontSize: 15, fontWeight: '700' },
  modalRoot: { flex: 1 },
  detailScroll: { flex: 1 },
  modalHeader: {
    paddingHorizontal: 18,
    paddingBottom: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  modalTitle: { flex: 1, marginHorizontal: 12, color: '#ffffff', fontSize: 18, fontWeight: '800' },
  detailContent: { padding: 16, gap: 14 },
  detailCard: { borderWidth: 1, borderRadius: 12, padding: 14 },
  sectionTitle: { fontSize: 17, fontWeight: '800', marginBottom: 10 },
  bigCode: {
    borderRadius: 12,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  bigCodeText: { fontSize: 28, fontWeight: '900', letterSpacing: 3 },
  primaryButton: {
    minHeight: 46,
    borderRadius: 10,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  primaryButtonText: { color: '#ffffff', fontSize: 14, fontWeight: '800' },
  commandGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  commandButton: {
    width: '48%',
    minHeight: 58,
    borderWidth: 1,
    borderRadius: 10,
    padding: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  commandText: { flex: 1, fontSize: 12, fontWeight: '700' },
  screenshotActions: { flexDirection: 'row', gap: 10, marginBottom: 12 },
  secondaryButton: {
    flex: 1,
    minHeight: 44,
    borderWidth: 1,
    borderRadius: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  secondaryButtonText: { fontSize: 13, fontWeight: '800' },
  screenshot: { width: '100%', height: 260, borderRadius: 10, backgroundColor: '#0f172a' },
  otaStatus: { borderRadius: 10, padding: 12, marginBottom: 4 },
  otaStatusText: { fontSize: 12, fontWeight: '700', marginBottom: 4 },
  busyRow: { alignItems: 'center', justifyContent: 'center', padding: 16 },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(15,23,42,0.55)',
    justifyContent: 'center',
    padding: 20,
  },
  createModal: { borderRadius: 16, padding: 18 },
  inputLabel: { fontSize: 12, fontWeight: '700', marginBottom: 6 },
  input: {
    minHeight: 46,
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 12,
  },
  modalActions: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 18 },
  cancelButton: {
    minHeight: 46,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
